import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Task } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_TASK_QUERY_KEY } from './tasks-query-keys';

/**
 * A single task. Nothing is seeded from here: this read is itself what
 * `addOrUpdateTaskCache` writes to, and the tasks list seeds it, so opening a
 * task from the list costs no second request.
 */
export function useFetchTask(taskId: string): {
    status: 'error' | 'success' | 'pending';
    task: Task | undefined;
    refetch: () => void;
} {
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_TASK_QUERY_KEY, taskId],
        queryFn: () => {
            return openApiRef.current.tasks.getTask(taskId).then((response) => {
                const task = response.data.data.task;
                return task;
            });
        },
        // Nothing to read before a task has been chosen.
        enabled: taskId !== ''
    });

    return {
        status,
        task: data,
        refetch: () => {
            void refetch();
        }
    };
}
